import { useCallback, useEffect, useState } from 'react';
import { ON_SCREEN_THRESHOLD, SEGMENT_DIVIDER_WIDTH } from 'src/shared/design-system/DishFirst/Carousel/consts';
import { CarouselProps } from 'src/shared/design-system/DishFirst/Carousel/types';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ScreenWidth, ScreenWidthValues } from 'web-common/src/shared/styles';

interface UseCarouselProps
  extends Pick<CarouselProps, 'content' | 'onScreenThreshold' | 'padding' | 'segmentDividerWidth'> {
  dividerRef?: HTMLDivElement | null;
  ref: HTMLDivElement | null;
}

export const useCarousel = (props: UseCarouselProps) => {
  const {
    content,
    dividerRef,
    segmentDividerWidth = SEGMENT_DIVIDER_WIDTH,
    onScreenThreshold = ON_SCREEN_THRESHOLD,
    padding = 0,
    ref,
  } = props;
  const {
    windowDimensions: { width },
  } = useScreenContext();

  const [numElementsLeft, setNumElementsLeft] = useState<number>();
  const [numElementsRight, setNumElementsRight] = useState<number>();
  const [scrolledElement, setScrolledElement] = useState<number>(0);

  const getSegmentDividerOffsetCss = useCallback(
    (scrollPercent: number) => {
      // Take offsets into account
      const leftPadding = (1 - scrollPercent) * padding;
      const dividerWidth = scrollPercent * segmentDividerWidth;
      const rightPadding = scrollPercent * padding;

      return `calc(${leftPadding}px + ${scrollPercent * 100}% - ${dividerWidth}px - ${rightPadding}px)`;
    },
    [padding, segmentDividerWidth]
  );
  // Have a bug where getBoundingClientRect is returning zero for all of its values.
  // This will ensure that we will call update again when the value changes.
  const rectx = ref?.getBoundingClientRect().x;

  useEffect(() => {
    if (!ref) {
      return;
    }

    const update = () => {
      const children = Array.from(ref.querySelectorAll<HTMLElement>(':scope > *'));
      const rect = ref.getBoundingClientRect();
      if (!rect?.width) {
        return;
      }
      const containerVisibleWidth = rect.right - rect.left;
      const isSmallScreen = width <= ScreenWidthValues[ScreenWidth.SMALL];
      if (isSmallScreen) {
        if (dividerRef) {
          const scrollPercent = ref.scrollLeft / (ref.scrollWidth - rect.width);
          // Need to imperatively react to scrolling to position the mobile divider
          // eslint-disable-next-line functional/immutable-data,no-param-reassign -- ^^^
          dividerRef.style.left = getSegmentDividerOffsetCss(scrollPercent);
        }
      }

      // This function responds to a scroll event listener and may be executed basically continuously
      //
      // Array.foreach() iterates n function calls which, in such a tight execution loop, can actually
      // cause noticeable lag to the user.
      //
      // As of 8/24/2022, using Array.foreach() instead of a for loop causes the segment divider to
      // freeze and then jump positions rather than moving smoothly

      // NOTE: $scrolledElement is undefined for nullish check
      // eslint-disable-next-line functional/no-let -- Transient counter vars
      let [$numElementsLeft, $numElementsRight]: number[] = [0, 0];

      // eslint-disable-next-line functional/no-loop-statement -- ^^^
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childRect = child.getBoundingClientRect();
        const visibleWidth = Math.min(rect.right, childRect.right) - Math.max(rect.left, childRect.left);
        if (visibleWidth <= onScreenThreshold && !isSmallScreen) {
          child.classList.add('offscreen');
        } else {
          child.classList.remove('offscreen');
        }

        // If _any part_ of child element is out of bounds
        if (childRect.left < rect.left) {
          $numElementsLeft++;
        }
        if (childRect.right > rect.right) {
          $numElementsRight++;
        }
      }
      setNumElementsLeft($numElementsLeft);
      setNumElementsRight($numElementsRight);

      const containerScrollDist = ref.scrollLeft;
      const containerTotalWidth = ref.scrollWidth;
      const scrollDistancePct = containerScrollDist / (containerTotalWidth - containerVisibleWidth);
      const selectedIndex = Math.min(Math.floor(scrollDistancePct * children.length), children.length - 1);
      setScrolledElement(selectedIndex);
    };

    update();
    ref.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      ref.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [
    ref,
    content, // Note: Pass in items to re-evaluate carousel contents on change (example: filters result in new items)
    dividerRef,
    width,
    onScreenThreshold,
    getSegmentDividerOffsetCss,
    scrolledElement,
    rectx,
  ]);

  const scrollLeft = useCallback(() => ref?.scrollBy({ left: -ref.clientWidth, behavior: 'smooth' }), [ref]);
  const scrollRight = useCallback(() => ref?.scrollBy({ left: ref.clientWidth, behavior: 'smooth' }), [ref]);

  const scrollTo = useCallback(
    (index) => {
      if (!ref) return;

      const children = Array.from(ref.querySelectorAll<HTMLElement>(':scope > *'));
      if (children?.[index]) {
        const containerTotalWidth = ref.scrollWidth;
        const rect = ref.getBoundingClientRect();
        const containerVisibleWidth = rect.right - rect.left;
        if (index === 0) {
          ref.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (index === children.length - 1) {
          ref.scrollTo({ left: containerTotalWidth - containerVisibleWidth, behavior: 'smooth' });
        } else {
          const item = children[index];
          const d = (index / children.length) * (containerTotalWidth - containerVisibleWidth) + item.clientWidth / 2;
          ref.scrollTo({ left: d, behavior: 'smooth' });
        }
      }
    },
    [ref]
  );

  return {
    scrollLeft,
    scrollRight,
    scrollTo,
    hasOverflow: !!(numElementsLeft && numElementsLeft > 0) || !!(numElementsRight && numElementsRight > 0),
    numElementsLeft,
    numElementsRight,
    scrolledElement,
    width,
  };
};
